import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./opened-filter-card.module.scss";
import {
  CardType,
  InvestmentOpportunity,
  LandPlot,
  SpaceForRent,
} from "../../../../interfaces/card";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Attachment, CutAndFill } from "../../../../interfaces/feature-info";
import {
  handleSelectCard,
  useCardNavigation,
} from "../../../../utils/cardNavigation";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import useGeneratePdf from "../../../../hooks/useGeneratePdf";
import useInfrastructure from "../../../../hooks/useInfrastructure";
import {
  clearScreenshots,
  setLayersVisible,
} from "../../../../services/reducers/map";
import {
  getAttachmentImageUrl,
  getCutAndFill,
  getFeatureInfo,
} from "../../../../services/api";
import { resetObjectStyle } from "../../../../utils/helpers-for-2d-map";
import {
  highlightSelectedObject,
  takeScreenshot,
} from "../../../../services/actions/map";
import { LayersDataTypes } from "../../../../types/layers-data-types";
import close from "../../../../images/close-gray.svg";
import {
  renderStatusTag,
  renderTypeOfUseTag,
} from "../../../../utils/render-tags";
import ImgSwiper from "../../../../shared/img-swiper/img-swiper";
import Button from "../../../ui-kit/button/button";
import EditAttachmentsModal from "../../../../shared/edit-attachments-modal/edit-attachments-modal";
import x from "../../../../images/x.svg";
import facebook from "../../../../images/facebook.svg";
import linkedin from "../../../../images/linkedin.svg";
import instagram from "../../../../images/instagram.svg";
import qrCode from "../../../../images/qr-code.svg";
import Modal from "../../../../shared/modal/modal";
import QrCode from "../../../../shared/qr-code/qr-code";
import ToggleSection from "../../../ui-kit/toggle-section/toggle-section";
import Infrastructure from "../../../map-controls/infrastructure/infrastructure";
import { generateLinkToEms } from "../../../../utils/genereate-link-to-ems";
import { createPortal } from "react-dom";
import CardSummary from "../card-summary/card-summary";
import chevron from "../../../../images/chevron-left.svg";
import { setInitialPlot } from "../../../../services/reducers/multiple-plot";
import Tooltip from "../../../ui-kit/tooltip/tooltip";
import ModalMultiplePlotList from "../../multiple-plot-list/modal-multiple-plot-list/modal-multiple-plot-list";
import AttachmentsModal from "../../../../shared/attachments-modal/attachments-modal";
import { LayerType } from "../../../../types/layer-type";
import { RoutePaths } from "../../../../config/routeConfig";
import SaveConfirmationModal from "./save-confirmation-modal/save-confirmation-modal";
import { fetchFilterData } from "../../../../services/reducers/filters";

interface UniversalCardFullProps {
  type: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
  card: InvestmentOpportunity | LandPlot | SpaceForRent;
  closeLeftPanel?: (value: boolean) => void;
}

type ModalKind = null | "edit" | "confirm" | "readonly";

const targetLayerTypes = [
  LayersDataTypes.Plots,
  LayersDataTypes.InvestmentOpportunities,
  LayersDataTypes.SpacesForRent,
];

const OpenedFilterCard: FC<UniversalCardFullProps> = ({
  type,
  card,
  closeLeftPanel,
}) => {
  const [cutAndFillImageUrl, setCutAndFillImageUrl] = useState<string | null>(
    null,
  );
  const [cutAndFill, setCutAndFill] = useState<CutAndFill | null>(null);
  const [documents, setDocuments] = useState<Attachment[]>([]);
  const [images, setImages] = useState<Attachment[]>([]);
  const [floorPlanImageUrl, setFloorPlanImageUrl] = useState<string | null>(
    null,
  );
  const [activeModal, setActiveModal] = useState<ModalKind>(null);
  const [qrCodeVisible, setQrCodeVisible] = useState<boolean>(false);
  const [isOpenInfrastructure, setIsOpenInfrastructure] =
    useState<boolean>(true);
  const [isOpenCutAndFill, setIsOpenCutAndFill] = useState<boolean>(true);
  const [isOpeFloorPlan, setIsOpenFloorPlan] = useState<boolean>(true);
  const [isPdfVisible, setIsPdfVisible] = useState<boolean>(false);
  const { layers, layersIds, visibleLayersId } = useAppSelector(
    (state) => state.map,
  );
  const user = useAppSelector((state) => state.user.user);
  const screenshots = useAppSelector((state) => state.map.screenshot);
  const [searchParams] = useSearchParams();
  const { initialPlot } = useAppSelector((state) => state.multiplePlotList);
  const [multiplePlotModalVisible, setMultiplePlotModalVisible] =
    useState<boolean>(false);
  const pdfPortal = document.getElementById("pdf-portal");
  const infrastructureData = useInfrastructure(card.geometry);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const generatePdf = useGeneratePdf();
  const { closeCurrentCard } = useCardNavigation();
  const navigate = useNavigate();
  const [floorPlanAttachment, setFloorPlanAttachment] =
    useState<Attachment | null>();
  const isMasterPlan = useMemo(
    () => window.location.href.includes(RoutePaths.masterPlan),
    [],
  );
  const { t } = useTranslation();

  useEffect(() => {
    setIsOpenInfrastructure(true);
    setIsOpenCutAndFill(true);
    setCutAndFill(null);
    setCutAndFillImageUrl(null);
    setDocuments([]);
    setImages([]);
    setFloorPlanImageUrl(null);
    setFloorPlanAttachment(null!);
    dispatch(clearScreenshots());
  }, [card.fid]);

  useEffect(() => {
    const currentFid = searchParams.get("fid");

    if (card.fid && currentFid !== String(card.fid)) {
      const params = new URLSearchParams(searchParams);
      params.set("fid", String(card.fid));
      navigate(`?${params.toString()}`, { replace: false });
      dispatch(
        highlightSelectedObject({ objectId: card.objectId, layer: card.layer }),
      );
    }
  }, [card.fid]);

  useEffect(() => {
    if (!card || !("layer" in card) || !card.layer?.id) return;

    const layerId = card.layer.id;
    const objectId = "objectId" in card ? card.objectId : null;

    if (!objectId) return;

    const fetchData = async () => {
      const res = await getFeatureInfo(layerId, objectId);
      if (!res) return;

      const attachments = res.attachments ?? [];
      setDocuments(attachments.filter((a: Attachment) => a.kindId === 2));
      setImages(
        attachments.filter((a: Attachment) => a.kindId === 1 && a.typeId === 3),
      );

      if (type === "InvestmentOpportunities" || type === "Plots") {
        const data = res.data ? JSON.parse(res.data) : null;
        const cutAndFillAttachment = attachments.find(
          (a: Attachment) => a.kindId === 1 && a.typeId === 1,
        );

        if (data) {
          setCutAndFill({
            landCut: data.LandCut,
            landFill: data.LandFill,
          });
        }

        if (cutAndFillAttachment) {
          const url = await getAttachmentImageUrl(cutAndFillAttachment.id);
          if (typeof url == "string") setCutAndFillImageUrl(url);
        }

        if (!data || !cutAndFillAttachment) {
          const additionalData = await getCutAndFill({ layerId, objectId });
          if (additionalData) {
            if (!data) {
              setCutAndFill({
                landCut: additionalData.landCut,
                landFill: additionalData.landFill,
              });
            }
            if (!cutAndFillAttachment && additionalData.image) {
              setCutAndFillImageUrl(
                `data:image/jpg;base64,${additionalData.image}`,
              );
            }
          }
        }
      } else if (type === "SpacesForRent") {
        const floorPlan = attachments.find(
          (a: Attachment) => a.kindId === 1 && a.typeId === 2,
        );
        if (floorPlan) {
          try {
            setFloorPlanAttachment(floorPlan);
            const url = await getAttachmentImageUrl(floorPlan.id);
            if (typeof url == "string") setFloorPlanImageUrl(url);
          } catch {
            setFloorPlanImageUrl(null);
          }
        } else {
          setFloorPlanAttachment(null);
          setFloorPlanImageUrl(null);
        }
      }
    };
    fetchData();
  }, [type, card]);

  const handleOpenAndDownloadPDF = () => {
    setIsPdfVisible(true);

    const allIds = targetLayerTypes.flatMap((type) => layersIds[type] ?? []);

    const missingIds = allIds.filter((id) => !visibleLayersId.includes(id));

    if (missingIds.length > 0) {
      dispatch(setLayersVisible([...visibleLayersId, ...missingIds]));
    }

    const filteredLayers = layers.filter(
      (l) =>
        targetLayerTypes.includes(l.dataType) &&
        l.type === LayerType.ArcgisFeatureServer,
    );

    if (card.geometry) {
      dispatch(
        takeScreenshot({
          name: `${card.objectId}`,
          width: 1602,
          height: 1326,
          zoomAdjustment: -1,
          layers: filteredLayers,
          geometry: card.geometry,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      highlightSelectedObject({ objectId: card.objectId, layer: card.layer }),
    );
    if (isPdfVisible && (screenshots?.dataUrl || card.geometry === null)) {
      if (pdfContainerRef.current) {
        generatePdf(pdfContainerRef.current);
        setIsPdfVisible(false);
      }
    }
  }, [isPdfVisible, screenshots?.dataUrl, generatePdf, dispatch]);

  const handleToggleVisibility = () => {
    if (closeLeftPanel) {
      closeLeftPanel(false);
    }
  };

  const closeCard = () => {
    if (card.fid) {
      closeCurrentCard(card.fid.toString());
    } else {
      closeCurrentCard(null);
    }
    resetObjectStyle();
  };

  const cardSummaryProps = useMemo(() => {
    const baseProps = { item: card };
    if (type === "InvestmentOpportunities") {
      return {
        ...baseProps,
        cutAndFill,
        cutAndFillImage: cutAndFillImageUrl,
        infrastructure: infrastructureData,
      };
    }
    if (type === "Plots") {
      return {
        ...baseProps,
        cutAndFill,
        cutAndFillImage: cutAndFillImageUrl,
        infrastructure: infrastructureData,
        key: card.fid,
      };
    }
    if (type === "SpacesForRent") {
      return { ...baseProps, floorPlan: floorPlanImageUrl };
    }
    return baseProps;
  }, [
    card,
    type,
    cutAndFill,
    cutAndFillImageUrl,
    floorPlanImageUrl,
    infrastructureData,
  ]);

  const qrCodeTitle = useMemo(() => {
    switch (type) {
      case "Plots":
        return `QR Code Plot Number: ${card.number}`;
      case "SpacesForRent":
        return `QR Code Office Number: ${card.number}`;
      case "InvestmentOpportunities":
        return `QR Code ${("description_e" in card && card.description_e) || "No Data"}`;
      default:
        return "";
    }
  }, [type, card]);

  const handleClickAddMultiplePlot = () => {
    if (initialPlot) {
      setMultiplePlotModalVisible(true);
    } else {
      dispatch(
        setInitialPlot({ plot: card as LandPlot, url: window.location.href }),
      );
      navigate("/multiplePlotList");
    }
  };

  const handleOpenEditModal = () => setActiveModal("edit");

  const handleOpenSavedModal = async (
    newType: CardType,
    fid: string,
    didChangeType: boolean,
  ) => {
    setActiveModal(null);

    try {
      if (didChangeType) {
        await Promise.all([
          dispatch(
            fetchFilterData({ filterType: "InvestmentOpportunities" }),
          ).unwrap(),
          dispatch(fetchFilterData({ filterType: "Plots" })).unwrap(),
          dispatch(fetchFilterData({ filterType: "SpacesForRent" })).unwrap(),
        ]);
      } else {
        await dispatch(fetchFilterData({ filterType: newType })).unwrap();
      }
    } catch (error) {
      console.error(error);
    }

    setActiveModal("confirm");

    handleSelectCard(dispatch, newType, fid, false);
  };

  const handleOpenReadonlyModal = () => setActiveModal("readonly");

  return (
    <>
      <div className={styles.card}>
        <div className={styles.container}>
          <CustomScrollbar height="100%" marginRight="16px">
            <div className={styles.scrollContent}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {type === "InvestmentOpportunities" && (
                    <span>
                      {("description_e" in card && card.description_e) ||
                        t("city-card.noData")}
                    </span>
                  )}
                  {type === "SpacesForRent" && (
                    <span>
                      {t("city-card.officeNumber")}: {card.number}
                    </span>
                  )}
                  {type === "Plots" && (
                    <span className={styles.plotNumber}>
                      {t("city-card.plotNumber")}: {card.number}
                    </span>
                  )}
                  {type === "Plots" && <p>{card.area} m²</p>}
                  <img
                    onClick={closeCard}
                    className={styles.closeBtn}
                    src={close}
                    alt="closeBtn"
                  />
                </div>
                {type !== "Plots" && (
                  <div className={styles.subTitle}>
                    {type === "InvestmentOpportunities" && (
                      <p>
                        {("description_a" in card && card.description_a) ||
                          t("city-card.noData")}
                      </p>
                    )}
                    {type === "SpacesForRent" && (
                      <p>
                        {t("city-card.buildingLabel")}{" "}
                        {"building" in card && card.building}
                      </p>
                    )}
                  </div>
                )}
                <div className={styles.tags}>
                  {type !== "InvestmentOpportunities" &&
                    renderStatusTag(card.status)}
                  {renderTypeOfUseTag(card.typeOfUse)}
                </div>
              </div>
              <ul className={styles.info}>
                <li>
                  {t("city-card.city")}: {card.city}
                </li>

                {type === "SpacesForRent" && (
                  <>
                    <li>
                      {t("city-card.floor")}:{" "}
                      {"floorName" in card && card.floorName}
                    </li>
                    <li>
                      {t("city-card.spaceArea")}: {card.area} m²
                    </li>
                  </>
                )}

                {type === "Plots" && (
                  <li>
                    {t("city-card.locationStatusCode")}:{" "}
                    {"locationStatusCode" in card && card.locationStatusCode}
                  </li>
                )}

                {type === "InvestmentOpportunities" && (
                  <>
                    <li>
                      {t("city-card.plotNumber")}: {card.number}
                    </li>
                    <li>
                      {t("city-card.plotArea")}: {card.area} m²
                    </li>
                  </>
                )}

                <li>{t("city-card.estimatedRent")}: 0.000 OMR</li>
              </ul>

              <div className={styles.swiper}>
                <ImgSwiper images={images} />
              </div>

              <div className={styles.topButtons}>
                {type === "Plots" && !user && (
                  <Button
                    type="outline"
                    className={styles.multipleBtn}
                    onClick={handleClickAddMultiplePlot}
                  >
                    {initialPlot?.fid === card.fid ? (
                      <>{t("city-card.deleteFromPlotList")}</>
                    ) : (
                      <>
                        {t("city-card.addToPlotList")}
                        <Tooltip text={t("city-card.plotListTooltip")} />
                      </>
                    )}
                  </Button>
                )}
                {user ? (
                  <Button type="outline" onClick={handleOpenEditModal}>
                    {type === "SpacesForRent"
                      ? t("city-card.manageSpace")
                      : t("city-card.managePlot")}
                  </Button>
                ) : (
                  <Button
                    disabled={!documents.length}
                    type="outline"
                    onClick={handleOpenReadonlyModal}
                  >
                    {!documents.length
                      ? t("city-card.noAttachments")
                      : t("city-card.seeAttachments")}
                  </Button>
                )}
              </div>

              <div className={styles.social}>
                <p>{t("city-card.share")}:</p>
                <div className={styles.links}>
                  {/*<span>*/}
                  {/*  <img className={styles.imgLink} src={x} alt="x" />*/}
                  {/*</span>*/}
                  {/*<span>*/}
                  {/*  <img*/}
                  {/*    className={styles.imgLink}*/}
                  {/*    src={facebook}*/}
                  {/*    alt="facebook"*/}
                  {/*  />*/}
                  {/*</span>*/}
                  {/*<span>*/}
                  {/*  <img*/}
                  {/*    className={styles.imgLink}*/}
                  {/*    src={linkedin}*/}
                  {/*    alt="linkedin"*/}
                  {/*  />*/}
                  {/*</span>*/}
                  {/*<span>*/}
                  {/*  <img*/}
                  {/*    className={styles.imgLink}*/}
                  {/*    src={instagram}*/}
                  {/*    alt="instagram"*/}
                  {/*  />*/}
                  {/*</span>*/}
                  <button
                    onClick={() => setQrCodeVisible(true)}
                    className={styles.qrCode}
                  >
                    <img className={styles.imgLink} src={qrCode} alt="qrCode" />
                  </button>
                </div>
              </div>

              {type !== "SpacesForRent" ? (
                <div className={styles.toggleSection}>
                  <ToggleSection
                    isOpen={isOpenInfrastructure}
                    onToggle={() =>
                      setIsOpenInfrastructure(!isOpenInfrastructure)
                    }
                    label={t("city-card.infrastructureAvailability")}
                  >
                    <Infrastructure infrastructure={infrastructureData} />
                  </ToggleSection>
                  <ToggleSection
                    label={t("city-card.cutAndFill")}
                    isOpen={isOpenCutAndFill}
                    onToggle={() => setIsOpenCutAndFill(!isOpenCutAndFill)}
                    tooltipText={t("city-card.cutAndFillTooltip")}
                  >
                    {cutAndFill?.landCut && cutAndFill?.landFill && (
                      <div className={styles.cutAndFill}>
                        <p>
                          {t("city-card.landCut")}: {cutAndFill.landCut} m³
                        </p>
                        <p>
                          {t("city-card.landFill")}: {cutAndFill.landFill} m³
                        </p>
                      </div>
                    )}
                    {cutAndFillImageUrl && (
                      <img
                        className={styles.cutAndFillImage}
                        src={cutAndFillImageUrl}
                        alt="cutAndFillImage"
                      />
                    )}
                  </ToggleSection>
                </div>
              ) : (
                <div className={styles.toggleSection}>
                  <ToggleSection
                    label={t("city-card.floorPlan")}
                    isOpen={isOpeFloorPlan}
                    onToggle={() => setIsOpenFloorPlan(!isOpeFloorPlan)}
                    tooltipText={t("city-card.floorPlanTooltip")}
                  >
                    {floorPlanImageUrl && (
                      <img
                        className={styles.floorPlan}
                        src={floorPlanImageUrl}
                        alt="floorPlanImage"
                      />
                    )}
                  </ToggleSection>
                </div>
              )}
            </div>
          </CustomScrollbar>
          <div className={styles.bottomButtons}>
            <Button type="outline" onClick={handleOpenAndDownloadPDF}>
              {t("city-card.getSummary")}
            </Button>
            {!isMasterPlan && (
              <Button
                type="primary"
                disabled={card.status === "Rented"}
                onClick={() =>
                  window.open(generateLinkToEms(card.estateEn, card.fid))
                }
              >
                {t("city-card.investHere")}
              </Button>
            )}
          </div>
        </div>
        {closeLeftPanel && (
          <button
            className={styles.toggleButton}
            onClick={handleToggleVisibility}
          >
            <img src={chevron} alt="Toggle panel" />
          </button>
        )}
      </div>
      {activeModal === "edit" && user && (
        <EditAttachmentsModal
          onClose={() => setActiveModal(null)}
          text={t("edit-attachments-modal.plotNumber", { number: card.number })}
          documents={documents}
          type={type}
          card={card}
          images={images}
          floor={
            type === "SpacesForRent"
              ? "floorName" in card && card.floorName
              : false
          }
          floorPlanAttachment={floorPlanAttachment ? floorPlanAttachment : null}
          layerId={String(card.layer.id)}
          featureId={String(card.objectId)}
          onSaved={handleOpenSavedModal}
        />
      )}
      {activeModal === "readonly" && (
        <AttachmentsModal
          onClose={() => setActiveModal(null)}
          text={
            type === "SpacesForRent"
              ? t("attachments-modal.types.officeNumber", {
                  number: card.number,
                })
              : t("attachments-modal.types.plotNumber", { number: card.number })
          }
          documents={documents}
        />
      )}
      {activeModal === "confirm" && (
        <SaveConfirmationModal
          type={type}
          card={card}
          text={t("edit-attachments-modal.plotNumber", { number: card.number })}
          onClose={() => setActiveModal(null)}
          onViewChanges={() => setActiveModal("edit")}
        />
      )}
      {qrCodeVisible && (
        <Modal onClose={() => setQrCodeVisible(false)}>
          <QrCode
            title={qrCodeTitle}
            href={window.location.href}
            from="search"
          />
        </Modal>
      )}
      {isPdfVisible &&
        pdfPortal &&
        createPortal(
          <div ref={pdfContainerRef}>
            <CardSummary
              screenshot={screenshots?.dataUrl ?? null}
              {...cardSummaryProps}
              type={type}
            />
          </div>,
          pdfPortal,
        )}
      {multiplePlotModalVisible && (
        <ModalMultiplePlotList
          type={initialPlot?.fid === card.fid ? "Delete" : "Add"}
          onClose={() => setMultiplePlotModalVisible(false)}
        />
      )}
    </>
  );
};

export default OpenedFilterCard;
